/**
 * 路由配置常量
 * 统一管理所有路由路径，便于维护和修改
 */

// 公开访问的官网路由（不需要登录）
export const PUBLIC_ROUTES = {
  // 基础路由 - 使用show/前缀
  HOME: '/show/main',
  PLATFORM_INTRO: '/show/platform-intro',
  INSTITUTIONS: '/show/institutions',
  SHOWCASE: '/show/showcase',
  BUSINESS: '/show/business',
  POLICY: '/show/policy',
  CONSULTATION: '/show/consultation',
  DEMAND: '/show/demand',
  
  // 带参数的路由
  INSTITUTION_DETAIL: '/show/institutions/:id',
  SHOWCASE_DETAIL: '/show/showcase/:id',
  NEWS_DETAIL: '/show/news/:id',
  POLICY_DETAIL: '/show/policy/:id',
  
  // 搜索和筛选路由
  INSTITUTIONS_SEARCH: '/show/institutions/search',
  SHOWCASE_SEARCH: '/show/showcase/search',
  NEWS_SEARCH: '/show/news/search',
  
  // 分类路由
  INSTITUTIONS_CATEGORY: '/show/institutions/category/:category',
  SHOWCASE_CATEGORY: '/show/showcase/category/:category',
  NEWS_CATEGORY: '/show/news/category/:category',
} as const;

// 兼容性：保持原有的FRONTEND_ROUTES
export const FRONTEND_ROUTES = PUBLIC_ROUTES;

// 需要鉴权的数据管理路由
export const AUTH_ROUTES = {
  // 认证路由
  LOGIN: '/login',
  
  // 数据管理主页面
  DATA_MANAGEMENT: '/data/home',
  
  // 数据管理子页面
  NEWS_MANAGEMENT: '/data/news',
  NEWS_EDIT: '/data/news/:id/edit',
  NEWS_CREATE: '/data/news/create',
  
  INSTITUTION_MANAGEMENT: '/data/institutions',
  INSTITUTION_EDIT: '/data/institutions/:id/edit',
  INSTITUTION_CREATE: '/data/institutions/create',
  
  SHOWCASE_MANAGEMENT: '/data/showcase',
  SHOWCASE_EDIT: '/data/showcase/:id/edit',
  SHOWCASE_CREATE: '/data/showcase/create',
  
  POLICY_MANAGEMENT: '/data/policy',
  POLICY_EDIT: '/data/policy/:id/edit',
  POLICY_CREATE: '/data/policy/create',
  
  BUSINESS_MANAGEMENT: '/data/business',
  BUSINESS_EDIT: '/data/business/:id/edit',
  BUSINESS_CREATE: '/data/business/create',
  
  DEMAND_MANAGEMENT: '/data/demand',
  DEMAND_EDIT: '/data/demand/:id/edit',
  DEMAND_CREATE: '/data/demand/create',
  
  // 系统管理
  USER_MANAGEMENT: '/data/users',
  ROLE_MANAGEMENT: '/data/roles',
  MENU_MANAGEMENT: '/data/menus',
  DICT_MANAGEMENT: '/data/dict',
  LOG_MANAGEMENT: '/data/log',
  PERMISSION_MANAGEMENT: '/data/permissions',
  SYSTEM_SETTINGS: '/data/settings',
} as const;

// 兼容性：保持原有的ADMIN_ROUTES
export const ADMIN_ROUTES = AUTH_ROUTES;

// 路由元信息配置
export const ROUTE_META = {
  [FRONTEND_ROUTES.HOME]: {
    title: '首页',
    description: '黄石经济技术开发区·铁山区知识产权服务业集聚区',
    keywords: '知识产权,服务业,集聚区,黄石'
  },
  [FRONTEND_ROUTES.PLATFORM_INTRO]: {
    title: '平台介绍',
    description: '了解知识产权服务业集聚区平台功能和服务',
    keywords: '平台介绍,知识产权服务,集聚区'
  },
  [FRONTEND_ROUTES.INSTITUTIONS]: {
    title: '入驻机构',
    description: '查看入驻知识产权服务机构信息',
    keywords: '入驻机构,知识产权服务,机构'
  },
  [FRONTEND_ROUTES.SHOWCASE]: {
    title: '精品展示',
    description: '展示优秀的知识产权成果',
    keywords: '精品展示,知识产权成果,专利,商标'
  },
  [FRONTEND_ROUTES.BUSINESS]: {
    title: '业务指南',
    description: '知识产权服务业务指南和流程',
    keywords: '业务指南,知识产权服务,流程'
  },
  [FRONTEND_ROUTES.POLICY]: {
    title: '政策法规',
    description: '知识产权相关政策法规',
    keywords: '政策法规,知识产权政策'
  },
  [FRONTEND_ROUTES.CONSULTATION]: {
    title: '咨询服务',
    description: '知识产权咨询服务',
    keywords: '咨询服务,知识产权咨询'
  }
} as const;

// 导航菜单配置
export const NAVIGATION_MENU = [
  {
    path: FRONTEND_ROUTES.HOME,
    label: '首页',
    icon: 'fas fa-home'
  },
  {
    path: FRONTEND_ROUTES.PLATFORM_INTRO,
    label: '平台介绍',
    icon: 'fas fa-info-circle'
  },
  {
    path: FRONTEND_ROUTES.INSTITUTIONS,
    label: '入驻机构',
    icon: 'fas fa-building'
  },
  {
    path: FRONTEND_ROUTES.SHOWCASE,
    label: '精品展示',
    icon: 'fas fa-star'
  },
  {
    path: FRONTEND_ROUTES.BUSINESS,
    label: '业务指南',
    icon: 'fas fa-book'
  },
  {
    path: FRONTEND_ROUTES.POLICY,
    label: '政策法规',
    icon: 'fas fa-gavel'
  },
  {
    path: FRONTEND_ROUTES.CONSULTATION,
    label: '咨询服务',
    icon: 'fas fa-phone'
  }
] as const;

// 数据管理导航菜单
export const DATA_MANAGEMENT_MENU = [
  {
    path: AUTH_ROUTES.NEWS_MANAGEMENT,
    label: '新闻管理',
    icon: 'fas fa-newspaper',
    description: '管理新闻文章和动态'
  },
  {
    path: AUTH_ROUTES.INSTITUTION_MANAGEMENT,
    label: '机构管理',
    icon: 'fas fa-building',
    description: '管理入驻机构信息'
  },
  {
    path: AUTH_ROUTES.SHOWCASE_MANAGEMENT,
    label: '展示管理',
    icon: 'fas fa-star',
    description: '管理精品展示内容'
  },
  {
    path: AUTH_ROUTES.POLICY_MANAGEMENT,
    label: '政策管理',
    icon: 'fas fa-gavel',
    description: '管理政策法规信息'
  },
  {
    path: AUTH_ROUTES.BUSINESS_MANAGEMENT,
    label: '业务管理',
    icon: 'fas fa-book',
    description: '管理业务指南内容'
  },
  {
    path: AUTH_ROUTES.USER_MANAGEMENT,
    label: '用户管理',
    icon: 'fas fa-users',
    description: '管理系统用户'
  },
  {
    path: AUTH_ROUTES.ROLE_MANAGEMENT,
    label: '角色管理',
    icon: 'fas fa-user-tag',
    description: '管理系统角色'
  },
  {
    path: AUTH_ROUTES.MENU_MANAGEMENT,
    label: '菜单管理',
    icon: 'fas fa-list',
    description: '管理系统菜单'
  },
  {
    path: AUTH_ROUTES.DICT_MANAGEMENT,
    label: '字典管理',
    icon: 'fas fa-book',
    description: '管理数据字典'
  },
  {
    path: AUTH_ROUTES.LOG_MANAGEMENT,
    label: '日志管理',
    icon: 'fas fa-file-alt',
    description: '查看系统操作日志'
  },
  {
    path: AUTH_ROUTES.PERMISSION_MANAGEMENT,
    label: '权限管理',
    icon: 'fas fa-shield-alt',
    description: '统一权限管理'
  },
  {
    path: AUTH_ROUTES.SYSTEM_SETTINGS,
    label: '系统设置',
    icon: 'fas fa-cog',
    description: '系统配置和设置'
  }
] as const;

// 兼容性：保持原有的ADMIN_NAVIGATION_MENU
export const ADMIN_NAVIGATION_MENU = DATA_MANAGEMENT_MENU;

// 路由类型定义
export type FrontendRoute = typeof FRONTEND_ROUTES[keyof typeof FRONTEND_ROUTES];
export type AdminRoute = typeof ADMIN_ROUTES[keyof typeof ADMIN_ROUTES];
export type RouteMeta = typeof ROUTE_META[keyof typeof ROUTE_META];
